import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface ClosurePeriodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (startDate: string, endDate: string, reason?: string, customerMessage?: string) => Promise<void>;
  initialData?: {
    startDate: string;
    endDate: string;
    reason?: string;
    customerMessage?: string;
  };
  mode: 'create' | 'edit';
}

export function ClosurePeriodDialog({
  open,
  onOpenChange,
  onSubmit,
  initialData,
  mode,
}: ClosurePeriodDialogProps) {
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [reason, setReason] = useState("");
  const [customerMessage, setCustomerMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  useEffect(() => {
    if (open && initialData) {
      setStartDate(new Date(initialData.startDate));
      setEndDate(new Date(initialData.endDate));
      setReason(initialData.reason || "");
      setCustomerMessage(initialData.customerMessage || "");
    } else if (open) {
      setStartDate(undefined);
      setEndDate(undefined);
      setReason("");
      setCustomerMessage("");
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    try {
      await onSubmit(
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd'),
        reason.trim() || undefined,
        customerMessage.trim() || undefined
      );
      onOpenChange(false);
    } catch (error) {
      // Error handled in hook
    } finally {
      setLoading(false);
    }
  };

  const isValid = startDate && endDate && endDate >= startDate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Ajouter une période de fermeture' : 'Modifier la période de fermeture'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Start Date */}
          <div className="grid gap-2">
            <Label htmlFor="startDate">Date de début</Label>
            <Popover open={startOpen} onOpenChange={setStartOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="startDate"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !startDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "PPP", { locale: fr }) : "Sélectionner une date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(date) => {
                    setStartDate(date);
                    setStartOpen(false);
                    // If end date is before start date, reset it
                    if (date && endDate && endDate < date) {
                      setEndDate(undefined);
                    }
                  }}
                  initialFocus
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* End Date */}
          <div className="grid gap-2">
            <Label htmlFor="endDate">Date de fin</Label>
            <Popover open={endOpen} onOpenChange={setEndOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="endDate"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !endDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "PPP", { locale: fr }) : "Sélectionner une date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={(date) => {
                    setEndDate(date);
                    setEndOpen(false);
                  }}
                  disabled={(date) => startDate ? date < startDate : false}
                  initialFocus
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Reason */}
          <div className="grid gap-2">
            <Label htmlFor="reason">Raison interne (optionnel)</Label>
            <Input
              id="reason"
              placeholder="Ex: Vacances d'été, Fermeture annuelle..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          {/* Customer Message */}
          <div className="grid gap-2">
            <Label htmlFor="customerMessage">Message affiché au client (optionnel)</Label>
            <Textarea
              id="customerMessage"
              placeholder="Ex: Fermé pour vacances d'été"
              value={customerMessage}
              onChange={(e) => setCustomerMessage(e.target.value)}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Si vide, le message par défaut sera : "Établissement fermé à cette date"
            </p>
          </div>

          {startDate && endDate && endDate < startDate && (
            <p className="text-sm text-destructive">
              La date de fin doit être postérieure ou égale à la date de début
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'create' ? 'Ajouter' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
